/**
 * What a buyer is told about being behind on their own loan.
 *
 * ---------------------------------------------------------------------------
 * This component exists because the customer portal used to answer these
 * questions for itself. Dashboard.js branched on `daysOverdue >= 8` -- a 7-day
 * grace period hardcoded into the BUYER's screen -- and then asserted
 * "A $75 late fee has been applied", where $75 was the tenant's configured
 * default (in fact a client-side constant), not the loan's own
 * late_fee_amount, and no waiver was consulted.
 *
 * The consequences, all of them things the buyer was told about their own money
 * that were not true:
 *   - a tenant with a 14-day grace period told its buyer on day 8 that a fee
 *     had been charged when the server had charged nothing;
 *   - a buyer whose fee had been deliberately waived was told they owed it;
 *   - the dollar figure was never the one on the loan.
 *
 * Everything here is rendered from the server's own flags, delivered on the
 * loan row by GET /api/loans and GET /api/loans/:id. Nothing in this file
 * computes a day count, compares a date, or decides whether a fee applies.
 * describeCustomerLateFee() reads those flags; that is the only reading.
 * ---------------------------------------------------------------------------
 */

import React from 'react';
import { describeCustomerLateFee, formatLoanDate } from '../utils/lateFeeDisplay';

const money = (n) => {
  const v = Number(n);
  return Number.isFinite(v)
    ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';
};

const TONE_ICON = {
  grace: '⏰',
  waived: '✅',
  due: '⚠️'
};

/**
 * The late fee, in the buyer's own terms. Three states, and the wording is
 * honest about which one they are in -- see describeCustomerLateFee().
 */
export function LateFeeNotice({ loan }) {
  if (loan?.alerts_disabled) return null;

  const notice = describeCustomerLateFee(loan);
  if (!notice.visible) return null;

  return (
    <div className={`loan-notice loan-notice-${notice.tone}`} role="status">
      <div className="loan-notice-heading">
        <span className="loan-notice-icon" aria-hidden="true">{TONE_ICON[notice.tone]}</span>
        <strong>{notice.heading}</strong>
      </div>
      {notice.body.map((line, i) => (
        <p className="loan-notice-line" key={i}>{line}</p>
      ))}
    </div>
  );
}

/**
 * A formal default/cure notice that has ACTUALLY been served.
 *
 * Gated on notice_sent_date, not on a day count. The block this replaces fired
 * at `daysOverdue >= 30` and announced "Your loan is officially in DEFAULT" --
 * on a loan whose status was still `active`, because the dashboard filters
 * defaulted loans out of the list entirely. Thirty days past due is not a legal
 * status; a served notice is an event with a date, a tracking number and a cure
 * deadline on the loan row.
 *
 * The fees quoted are the ones actually charged: the tenant's resolved notice
 * fee (effective_notice_fee_amount) and the real postage recorded when the
 * notice was mailed (notice_postal_cost).
 */
export function DefaultNoticeStatus({ loan, companyPhone, companyEmail }) {
  if (!loan?.notice_sent_date) return null;

  const noticeFee = Number(loan.effective_notice_fee_amount) || 0;
  const postal = Number(loan.notice_postal_cost) || 0;
  const cureDate = formatLoanDate(loan.cure_deadline_date);

  return (
    <div className="loan-notice loan-notice-notice" role="status">
      <div className="loan-notice-heading">
        <span className="loan-notice-icon" aria-hidden="true">📬</span>
        <strong>We have sent you a formal notice</strong>
      </div>

      <p className="loan-notice-line">
        A default and cure notice was mailed to you on {formatLoanDate(loan.notice_sent_date)}.
        {loan.notice_tracking_number
          ? ` Certified mail tracking number ${loan.notice_tracking_number}.`
          : ''}
      </p>

      {(noticeFee > 0 || postal > 0) && (
        <p className="loan-notice-line">
          Sending it added{' '}
          {noticeFee > 0 && <>a ${money(noticeFee)} notice fee</>}
          {noticeFee > 0 && postal > 0 && ' and '}
          {postal > 0 && <>${money(postal)} in certified postage</>}
          {' '}to your balance.
        </p>
      )}

      {cureDate && (
        <p className="loan-notice-line loan-notice-emphasis">
          To keep your contract you need to bring the account current by {cureDate}.
        </p>
      )}

      <p className="loan-notice-line">
        This is the point to talk to us. If you cannot pay the full amount by then,
        call {companyPhone} or email {companyEmail}. An arrangement made before the
        deadline is worth far more than one made after it.
      </p>
    </div>
  );
}

/**
 * The loan is actually in default.
 *
 * Gated on status, which is the only thing that makes this statement true. The
 * old dashboard printed "Your loan is officially in DEFAULT" at 30 days past
 * due on loans whose status was still `active` -- and never on a genuinely
 * defaulted one, because those are filtered out of the dashboard list. This is
 * the opposite: it says nothing at 30 days, and says it plainly when it is so.
 *
 * No late fee copy accompanies this. is_overdue is false once a loan leaves
 * `active`, which is correct -- there is no next payment to be late for any
 * more, and quoting a grace period against a defaulted contract would be
 * meaningless.
 */
export function DefaultedStatus({ loan, companyPhone, companyEmail }) {
  if (loan?.status !== 'defaulted') return null;

  const declaredOn = formatLoanDate(loan.default_date);

  return (
    <div className="loan-notice loan-notice-notice" role="status">
      <div className="loan-notice-heading">
        <span className="loan-notice-icon" aria-hidden="true">📄</span>
        <strong>This contract is in default</strong>
      </div>

      <p className="loan-notice-line">
        {declaredOn
          ? `Your contract was declared in default on ${declaredOn}.`
          : 'Your contract has been declared in default.'}
        {loan.next_payment_date
          ? ` The last payment due on it was ${formatLoanDate(loan.next_payment_date)}.`
          : ''}
      </p>

      <p className="loan-notice-line loan-notice-emphasis">
        Please get in touch with us: call {companyPhone} or email {companyEmail}.
      </p>

      <p className="loan-notice-line">
        We would rather talk than not. If your circumstances have changed, tell us
        what has happened and we will go through what is still possible.
      </p>
    </div>
  );
}

/**
 * All of it, in the order a worried buyer should read it: what is owed right
 * now, then what has formally happened.
 */
export default function LoanStatusNotices({ loan, companyPhone, companyEmail }) {
  return (
    <>
      <LateFeeNotice loan={loan} />
      <DefaultNoticeStatus
        loan={loan}
        companyPhone={companyPhone}
        companyEmail={companyEmail}
      />
      <DefaultedStatus
        loan={loan}
        companyPhone={companyPhone}
        companyEmail={companyEmail}
      />
    </>
  );
}
